import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AppShell } from './components/AppShell';
import { Empty, Loading } from './components/ui';
import { useAuth } from './lib/auth';
import type { Privilege } from './lib/types';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Pipeline } from './pages/Pipeline';
import { NewApplication } from './pages/NewApplication';
import { ApplicationDetailPage } from './pages/ApplicationDetail';
import { Underwriting } from './pages/Underwriting';
import { Merchants } from './pages/Merchants';
import { Equipment } from './pages/Equipment';
import { Supplies } from './pages/Supplies';
import { Reports } from './pages/Reports';
import { Organization } from './pages/Organization';
import { Permissions } from './pages/Permissions';
import { People } from './pages/People';
import { Security } from './pages/Security';
import { Settings } from './pages/Settings';
import { RecycleBin } from './pages/RecycleBin';
import { MfaSetup } from './pages/MfaSetup';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { ResetPassword } from './pages/ResetPassword';
import './pages/shared.css';

/**
 * Sends an unauthenticated visitor to the sign-in screen, then back again —
 * and holds an account with no second factor on the setup screen.
 *
 * That last part is not decoration: the API refuses every other route until a
 * factor is confirmed, so without this the app would render screens whose
 * every request comes back 403.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, needsMfaSetup } = useAuth();
  const location = useLocation();

  if (loading) return <Loading label="Restoring your session…" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (needsMfaSetup) return <MfaSetup />;
  return <>{children}</>;
}

/**
 * Guards a route behind a privilege. The sidebar already hides what a role
 * cannot reach, so this catches a typed URL or a stale bookmark.
 */
function RequirePrivilege({
  privilege,
  children,
}: {
  privilege: Privilege;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(privilege)) {
    return (
      <Empty
        title="Outside your scope"
        detail="Your role does not hold the privilege this screen needs. A parent office can grant it."
      />
    );
  }
  return <>{children}</>;
}

export function App() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          loading ? (
            <Loading label="Restoring your session…" />
          ) : user ? (
            <Navigate to="/" replace />
          ) : (
            <Login />
          )
        }
      />

      {/* Claimed from an emailed link, so neither may require a session. */}
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />

        <Route
          path="applications"
          element={
            <RequirePrivilege privilege="application.write">
              <Pipeline />
            </RequirePrivilege>
          }
        />
        <Route
          path="applications/new"
          element={
            <RequirePrivilege privilege="application.write">
              <NewApplication />
            </RequirePrivilege>
          }
        />
        <Route path="applications/:id" element={<ApplicationDetailPage />} />

        <Route
          path="underwriting"
          element={
            <RequirePrivilege privilege="application.decide">
              <Underwriting />
            </RequirePrivilege>
          }
        />

        <Route path="merchants" element={<Merchants />} />

        <Route
          path="equipment"
          element={
            <RequirePrivilege privilege="equipment.order">
              <Equipment />
            </RequirePrivilege>
          }
        />
        <Route
          path="supplies"
          element={
            <RequirePrivilege privilege="supplies.order">
              <Supplies />
            </RequirePrivilege>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePrivilege privilege="residual.view">
              <Reports />
            </RequirePrivilege>
          }
        />

        <Route path="organization" element={<Organization />} />
        <Route
          path="people"
          element={
            <RequirePrivilege privilege="org.manage_users">
              <People />
            </RequirePrivilege>
          }
        />
        <Route
          path="permissions"
          element={
            <RequirePrivilege privilege="org.invite">
              <Permissions />
            </RequirePrivilege>
          }
        />
        <Route
          path="settings"
          element={
            <RequirePrivilege privilege="org.move_node">
              <Settings />
            </RequirePrivilege>
          }
        />
        <Route
          path="recycle-bin"
          element={
            <RequirePrivilege privilege="org.manage_users">
              <RecycleBin />
            </RequirePrivilege>
          }
        />
        {/* Your own account, so no privilege gates it. */}
        <Route path="security" element={<Security />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
